import {
    EmbedEvent,
    EmbedEvents,
    MembersUpdatedEvent,
} from 'embed/types/events'

import {
    NinchatChannelJoinedEvent,
    NinchatChannelMemberJoinedEvent,
    NinchatChannelMemberPartedEvent,
    NinchatEventType,
    NinchatUserUpdatedEvent,
} from 'api/types/events'
import {RootState} from 'store'
import {UserPresence} from 'types/members'

import {channelJoined} from 'api/event/channelJoined'
import {config} from 'embed/listeners'
import {ninchatEventSlice} from 'api/slice'
import {startAppListening} from 'middlewares/listener'

type Effect = Parameters<typeof startAppListening>[0]['effect']
type Predicate = Parameters<typeof startAppListening>[0]['predicate']

const userUpdatedType = `${ninchatEventSlice.actions.user_updated}`
const channelMemberJoinedType = `${ninchatEventSlice.actions.channel_member_joined}`
const channelMemberPartedType = `${ninchatEventSlice.actions.channel_member_parted}`
const channelJoinedType = channelJoined.fulfilled.type

const getUserPresence = (connected?: boolean, idle?: number): UserPresence => {
    if (connected && Boolean(idle)) {
        return 'idle'
    }
    if (connected) {
        return 'online'
    }
    return 'offline'
}

const buildMembersEvent = (
    channel_id: string,
    state: RootState,
    targetOrigin: string,
): MembersUpdatedEvent | undefined => {
    const channel = state.ninchat.user_channels[channel_id]
    if (!channel?.channel_attrs?.audience_id) {
        return undefined
    }

    const {
        _users, user_id: selfUserId,
    } = state.ninchat

    const members = Object.keys(channel.channel_members ?? {})
        .filter((memberId) => memberId !== selfUserId && !!_users[memberId]?.user_attrs)
        .map((memberId) => {
            const userAttrs = _users[memberId].user_attrs
            return {
                id: memberId,
                name: userAttrs?.name,
                presence: getUserPresence(userAttrs?.connected, userAttrs?.idle),
            }
        })

    return {
        channel_id,
        event: EmbedEvent.MembersUpdated,
        members,
        targetOrigin,
    }
}

export const membersUpdatedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: (action, listenerApi) => {
        const targetOrigin = config.get('targetOrigin')
        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const state = listenerApi.getState()

        if (action.type === userUpdatedType) {
            const {user_id: updatedUserId} = action.payload as NinchatUserUpdatedEvent

            Object.keys(state.ninchat.user_channels).forEach((channel_id) => {
                if (!state.ninchat.user_channels[channel_id].channel_members?.[updatedUserId]) {
                    return
                }
                const event = buildMembersEvent(channel_id, state, targetOrigin)
                if (event) {
                    sendToParent(event)
                }
            })
        } else if (action.type === channelJoinedType) {
            const payload = Array.isArray(action.payload)
                ? action.payload.find((p: NinchatChannelJoinedEvent) => p.event === NinchatEventType.NinchatChannelJoinedEvent)
                : action.payload
            const {channel_id} = payload
            const event = buildMembersEvent(channel_id, state, targetOrigin)
            if (event) {
                sendToParent(event)
            }
        } else {
            const {channel_id} = action.payload as NinchatChannelMemberJoinedEvent | NinchatChannelMemberPartedEvent
            const event = buildMembersEvent(channel_id, state, targetOrigin)
            if (event) {
                sendToParent(event)
            }
        }
    },
    predicate: (action) => action.type === userUpdatedType ||
        action.type === channelMemberJoinedType ||
        action.type === channelMemberPartedType ||
        action.type === channelJoinedType,
})

export const start = (sendToParent: (event: EmbedEvents) => void) => [
    startAppListening(membersUpdatedListener(sendToParent)),
]
